import dayjs from 'dayjs'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexEntries,
} from 'features/rolodex'
import { ChartDataPoint, DataLayer, TimeRange } from 'features/systemLens'
import {
  useResolvedChart,
  useTimeSeriesDataProcessing,
} from 'features/timeSeries'
import { DEFAULT_DATA_LAYERS } from 'features/timeSeries/config/DataLayers'
import { useApplyChartConfig } from 'features/timeSeries/hooks/useApplyChartConfig'
import { useChartSave } from 'features/timeSeries/hooks/useChartSave'
import { useDerivedValues } from 'features/timeSeries/hooks/useDerivedValues'
import { useTimeSeriesUX } from 'features/timeSeries/hooks/useTimeSeriesUX'
import { RegressionResult } from 'features/timeSeries/regressionHelpers'
import {
  buildDateConfigFromState,
  buildSourceConfigsFromState,
  buildYAxisModeFromState,
} from 'features/timeSeries/utils/chartStateBuilders'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateMode,
  LocalThresholdOverrideType,
  RegressionConfig,
  RolodexEntryType,
  SensorAxisAssignment,
  SensorAxisMapping,
  SensorEntryWrapper,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { CursorDataProvider } from './CursorDataContext'

interface ScatterplotProviderProps {
  children: ReactNode
  chartID: string
  siteID: string
  disableSaving?: boolean
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
}

interface ScatterplotProviderReturnType {
  siteID: string
  organizationID: string
  chartID: string
  chartData: ChartDataPoint[]
  interpolate: boolean
  setInterpolate: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  liveTail: boolean
  setLiveTail: Dispatch<SetStateAction<boolean>>
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  setDerivedValues: Dispatch<SetStateAction<TimeSeriesDerivedValueEntryJSON[]>>
  regressionConfigs: RegressionConfig[]
  setRegressionConfigs: Dispatch<SetStateAction<RegressionConfig[]>>
  regressionResults: Record<string, RegressionResult>
  setRegressionResults: Dispatch<
    SetStateAction<Record<string, RegressionResult>>
  >
  unitMapping: Record<string, string>
  sensorRanges: Record<string, { min: number; max: number; unit: string }>
  localThresholdOverrides: LocalThresholdOverrideType
  setLocalThresholdOverrides: Dispatch<
    SetStateAction<LocalThresholdOverrideType>
  >
  thresholdFilters: Record<string, ThresholdFilterConfig>
  setThresholdFilters: Dispatch<
    SetStateAction<Record<string, ThresholdFilterConfig>>
  >
  smoothingConfigs: Record<string, SmoothingConfig>
  setSmoothingConfigs: Dispatch<SetStateAction<Record<string, SmoothingConfig>>>
  singleScaleMode: boolean
  autoScaleMode: boolean
  handleScaleModeToggle: (newSingleScaleMode: boolean) => void
  handleAutoScaleModeToggle: (newAutoScaleMode: boolean) => void
  connectPoints: boolean
  setConnectPoints: Dispatch<SetStateAction<boolean>>
  showXAxis: boolean
  setShowXAxis: Dispatch<SetStateAction<boolean>>
  xAxisSensorId: string | null
  setXAxisSensorId: Dispatch<SetStateAction<string | null>>
  customAxisRanges: Record<string, { min?: number; max?: number }>
  customXAxisRange: { min?: number; max?: number }
  setCustomXAxisRange: Dispatch<SetStateAction<{ min?: number; max?: number }>>
  sensorAxisMapping: SensorAxisMapping
  fetchRawData: ReturnType<typeof useTimeSeriesDataProcessing>['fetchRawData']
  handleRemoveDerivedValue: (id: string) => Promise<void>
  createDerivedValues: (
    newDerived: TimeSeriesDerivedValueEntryJSON,
    editingDerivedId: string | null
  ) => Promise<TimeSeriesDerivedValueEntryJSON[]>
  dataLayers: DataLayer[]
  setDataLayers: Dispatch<SetStateAction<DataLayer[]>>
  setCustomAxisRanges: Dispatch<
    SetStateAction<Record<string, { min?: number; max?: number }>>
  >
  expandedAxes: Record<string, boolean>
  setExpandedAxes: Dispatch<SetStateAction<Record<string, boolean>>>
  setSensorAxisAssignment: Dispatch<SetStateAction<SensorAxisAssignment>>
  handleYMinChange: (sensorId: string, value: string) => void
  handleYMaxChange: (sensorId: string, value: string) => void
  timeRange: TimeRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  selectedSensors: BaseSensorEntry[]
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  sensorEntries: BaseSensorEntry[]
  dateMode: DateMode
  setDateMode: React.Dispatch<React.SetStateAction<DateMode>>
  customSensorColors: Record<string, string>
  handleChangeColor: (sensorId: string, color: string) => void
  sensorVisibilityConfig: Record<string, boolean>
  setSensorVisibilityConfig: Dispatch<SetStateAction<Record<string, boolean>>>
  relativeStart: { value?: number; unit: ChartTimeUnit }
  setRelativeStart: Dispatch<
    SetStateAction<{ value?: number; unit: ChartTimeUnit }>
  >
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  setRelativeEnd: Dispatch<
    SetStateAction<{ isNow: boolean; value?: number; unit: ChartTimeUnit }>
  >
  cadenceSeconds: number
  setCadenceSeconds: Dispatch<SetStateAction<number>>
  saveChart: () => Promise<void>
  revertChanges: () => void
  hasUnsavedChanges: boolean
  lastSavedAt: Date | null
  isResizing: boolean
  setIsResizing: Dispatch<SetStateAction<boolean>>
  highlightedSensorId: string | null
  setHighlightedSensorId: Dispatch<SetStateAction<string | null>>
}

export const ScatterplotProvider = ({
  children,
  chartID,
  siteID,
  disableSaving = false,
  onChartSaved,
}: ScatterplotProviderProps) => {
  const [dataLayers, setDataLayers] = useState<DataLayer[]>(DEFAULT_DATA_LAYERS)
  const [connectPoints, setConnectPoints] = useState(false)
  const [showXAxis, setShowXAxis] = useState(true)
  const [isResizing, setIsResizing] = useState(false)
  const { errorNotification } = useGeckoNotifications()

  const { getSite } = useRolodexEntries(
    siteID ? { ids: [siteID], type: [RolodexEntryType.SITE] } : undefined
  )
  const orgID = useMemo(() => {
    const site = getSite(siteID)
    return site?.tags.organization ?? ''
  }, [siteID, getSite])

  const { sites } = useRolodexEntries({
    type: RolodexEntryType.SITE,
    ids: [siteID],
    limit: 1,
  })

  const organizationID = useMemo(() => {
    return sites.at(0)?.tags.organization ?? ''
  }, [sites])

  const { chart } = useResolvedChart(disableSaving ? undefined : chartID, {
    onError: (error) => {
      errorNotification(`Failed to resolve chart ${chartID}: ${error.message}`)
    },
  })

  const { data: allSensorEntryWrappers } = useGetRolodexEntriesByLoadOptions<
    SensorEntryWrapper[]
  >({
    type: [RolodexEntryType.DATA_POINTS],
    tags: { site: [siteID] },
    limit: 10000,
  })

  const { derivedValues: derivedValueEntries } = useDerivedValues({
    tags: { site: [siteID] },
  })

  const sensorEntries = useMemo(
    () => allSensorEntryWrappers?.map((entry) => entry.entry) ?? [],
    [allSensorEntryWrappers]
  )

  const [selectedSensors, setSelectedSensors] = useState<BaseSensorEntry[]>([])
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    const now = new Date().toISOString()
    return { from: dayjs(now).subtract(7, 'days').toISOString(), to: now }
  })

  // Track derived value IDs that should be deleted on save
  const derivedValueIdsToDeleteRef = useRef<Set<string>>(new Set())

  const [relativeStart, setRelativeStart] = useState<{
    value?: number
    unit: ChartTimeUnit
  }>({
    value: 7,
    unit: 'days',
  })
  const [relativeEnd, setRelativeEnd] = useState<{
    isNow: boolean
    value?: number
    unit: ChartTimeUnit
  }>({
    isNow: true,
    value: 0,
    unit: 'days',
  })

  const {
    chartData,
    interpolate,
    setInterpolate,
    isLoading,
    liveTail,
    setLiveTail,
    derivedValues,
    setDerivedValues,
    regressionConfigs,
    setRegressionConfigs,
    regressionResults,
    setRegressionResults,
    unitMapping,
    localThresholdOverrides,
    setLocalThresholdOverrides,
    thresholdFilters,
    setThresholdFilters,
    smoothingConfigs,
    setSmoothingConfigs,
    fetchRawData,
    handleRemoveDerivedValue,
    createDerivedValues,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    dateMode,
    setDateMode,
    customSensorColors,
    setCustomSensorColors,
    handleChangeColor,
    cadenceSeconds,
    setCadenceSeconds,
  } = useTimeSeriesDataProcessing({
    sensorEntries,
    selectedSensors,
    setSelectedSensors,
    timeRange,
    setTimeRange,
    dataLayers,
    siteID,
    orgID,
    derivedValueIdsToDeleteRef,
    relativeStart,
    relativeEnd,
  })

  const {
    singleScaleMode,
    setSingleScaleMode,
    autoScaleMode,
    setAutoScaleMode,
    handleScaleModeToggle,
    handleAutoScaleModeToggle,
    xAxisSensorId,
    setXAxisSensorId,
    customAxisRanges,
    customXAxisRange,
    setCustomXAxisRange,
    sensorAxisMapping,
    setCustomAxisRanges,
    expandedAxes,
    setExpandedAxes,
    setSensorAxisAssignment,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    highlightedSensorId,
    setHighlightedSensorId,
  } = useTimeSeriesUX({
    selectedSensors,
    derivedValues,
    unitMapping,
    chartData,
  })

  const { applyChartConfig } = useApplyChartConfig()

  useEffect(() => {
    applyChartConfig({
      chart,
      sensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode: 'merge',
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
    })
  }, [
    chart,
    sensorEntries,
    derivedValueEntries,
    cadenceSeconds,
    applyChartConfig,
    setSelectedSensors,
    setDerivedValues,
    setCustomSensorColors,
    setSensorVisibilityConfig,
    setCustomAxisRanges,
    setAutoScaleMode,
    setSingleScaleMode,
    setDateMode,
    setTimeRange,
    setLiveTail,
    setRelativeStart,
    setRelativeEnd,
    setCadenceSeconds,
  ])

  // Build current chart config from live state
  const computedSourceConfigs = useMemo(
    () =>
      buildSourceConfigsFromState(
        selectedSensors,
        customSensorColors,
        derivedValues,
        customAxisRanges,
        sensorVisibilityConfig,
        autoScaleMode
      ),
    [
      selectedSensors,
      customSensorColors,
      derivedValues,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode,
    ]
  )

  const computedYAxisMode = useMemo(
    () => buildYAxisModeFromState(singleScaleMode),
    [singleScaleMode]
  )

  const computedDateConfig = useMemo(
    () =>
      buildDateConfigFromState(
        dateMode,
        timeRange,
        relativeStart,
        relativeEnd,
        cadenceSeconds
      ),
    [dateMode, timeRange, relativeStart, relativeEnd, cadenceSeconds]
  )

  const { saveChart, hasUnsavedChanges, lastSavedAt } = useChartSave({
    chartID: chartID,
    selectedSourceConfigs: computedSourceConfigs,
    yAxisMode: computedYAxisMode,
    dateConfig: computedDateConfig,
    tags: {
      site: siteID,
      organization: orgID,
    },
    derivedValueIdsToDeleteRef: derivedValueIdsToDeleteRef,
    onChartSaved: onChartSaved,
  })

  const revertChanges = useCallback(() => {
    derivedValueIdsToDeleteRef.current.clear()

    applyChartConfig({
      chart,
      sensorEntries,
      derivedValueEntries,
      cadenceSeconds,
      mode: 'replace',
      setSelectedSensors,
      setDerivedValues,
      setCustomSensorColors,
      setSensorVisibilityConfig,
      setCustomAxisRanges,
      setAutoScaleMode,
      setSingleScaleMode,
      setDateMode,
      setTimeRange,
      setLiveTail,
      setRelativeStart,
      setRelativeEnd,
      setCadenceSeconds,
    })
  }, [
    chart,
    sensorEntries,
    derivedValueEntries,
    cadenceSeconds,
    applyChartConfig,
    setSelectedSensors,
    setDerivedValues,
    setCustomSensorColors,
    setSensorVisibilityConfig,
    setCustomAxisRanges,
    setAutoScaleMode,
    setSingleScaleMode,
    setDateMode,
    setTimeRange,
    setLiveTail,
    setRelativeStart,
    setRelativeEnd,
    setCadenceSeconds,
  ])

  return (
    <CursorDataProvider>
      <ScatterplotContext.Provider
        value={{
          siteID,
          organizationID,
          chartID,
          chartData,
          interpolate,
          setInterpolate,
          isLoading,
          liveTail,
          setLiveTail,
          derivedValues,
          setDerivedValues,
          regressionConfigs,
          setRegressionConfigs,
          regressionResults,
          setRegressionResults,
          unitMapping,
          sensorRanges,
          localThresholdOverrides,
          setLocalThresholdOverrides,
          thresholdFilters,
          setThresholdFilters,
          smoothingConfigs,
          setSmoothingConfigs,
          singleScaleMode,
          autoScaleMode,
          handleScaleModeToggle,
          handleAutoScaleModeToggle,
          connectPoints,
          setConnectPoints,
          showXAxis,
          setShowXAxis,
          xAxisSensorId,
          setXAxisSensorId,
          customAxisRanges,
          customXAxisRange,
          setCustomXAxisRange,
          sensorAxisMapping,
          fetchRawData,
          handleRemoveDerivedValue,
          createDerivedValues,
          dataLayers,
          setDataLayers,
          setCustomAxisRanges,
          expandedAxes,
          setExpandedAxes,
          setSensorAxisAssignment,
          handleYMinChange,
          handleYMaxChange,
          timeRange,
          setTimeRange,
          selectedSensors,
          setSelectedSensors,
          sensorEntries,
          sensorVisibilityConfig,
          setSensorVisibilityConfig,
          dateMode,
          setDateMode,
          relativeStart,
          setRelativeStart,
          relativeEnd,
          setRelativeEnd,
          customSensorColors,
          handleChangeColor,
          cadenceSeconds,
          setCadenceSeconds,
          saveChart,
          revertChanges,
          hasUnsavedChanges,
          lastSavedAt,
          isResizing,
          setIsResizing,
          highlightedSensorId,
          setHighlightedSensorId,
        }}>
        {children}
      </ScatterplotContext.Provider>
    </CursorDataProvider>
  )
}

export const ScatterplotContext = createContext<ScatterplotProviderReturnType>(
  null!
)

export const useScatterplotContext = (): ScatterplotProviderReturnType => {
  const context = useContext(ScatterplotContext)
  if (!context) {
    throw new Error('useScatterplot must be used within a ScatterplotProvider')
  }
  return context
}
